import { LoggingService } from '@core/services/logging.service';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Production Desktop Notification Service
 *
 * Handles desktop notifications for the Scan Ninja application.
 * Used for critical security alerts, scan completion notifications,
 * and other important system events.
 */

export interface ToastNotification {
  id: string;
  title: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  duration?: number; // in milliseconds, 0 = permanent
  action?: {
    label: string;
    callback: () => void;
  };
  timestamp: Date;
}

export interface DesktopNotificationOptions {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  tag?: string;
  data?: any;
  requireInteraction?: boolean;
  silent?: boolean;
  vibrate?: number[];
}

@Injectable({
  providedIn: 'root',
})
export class ToastNotificationService {
  private loggingService = inject(LoggingService);

  private notifications$ = new BehaviorSubject<ToastNotification[]>([]);
  private defaultDuration = 6000; // 6 seconds - reasonable time to read but not intrusive
  private notificationPermission: NotificationPermission = 'default';
  private permissionState$ = new BehaviorSubject<NotificationPermission>(
    'default',
  );
  private pendingCallbacks = new Map<string, () => void>(); // Store callbacks separately
  private ongoingPermissionRequest: Promise<boolean> | null = null; // Track ongoing request
  // Throttling flags to prevent repetitive logs
  private warnedUnsupported = false;
  private warnedPermissionNotGranted = false;

  constructor() {
    this.checkNotificationPermission();
  }

  /**
   * Check and request notification permission
   * @returns Promise<void>
   */
  private async checkNotificationPermission(): Promise<void> {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      this.notificationPermission = Notification.permission;
      this.permissionState$.next(this.notificationPermission);
    }
  }

  /**
   * Get notification permission status
   * @returns NotificationPermission - Current notification permission status ('granted', 'denied', or 'default')
   */
  getNotificationPermission(): NotificationPermission {
    const permission = this.notificationPermission;
    return permission;
  }

  /**
   * Observable stream of the current notification permission.
   * Useful for UI components to react to permission changes without showing intrusive toasts.
   */
  getPermission$(): Observable<NotificationPermission> {
    return this.permissionState$.asObservable();
  }

  /**
   * Request notification permission
   * Implements quiet-fail policy: no user-facing error/warning toasts when permission is denied/dismissed or request fails.
   * @returns Promise<boolean> - true if permission granted, false otherwise
   */
  async requestNotificationPermission(): Promise<boolean> {
    // Requesting notification permission
    // If there's already an ongoing request, wait for it but with a timeout
    if (this.ongoingPermissionRequest) {
      try {
        // Waiting for ongoing permission request
        // Wait for the ongoing request but with a 5-second timeout
        const timeoutPromise = new Promise<boolean>((_, reject) => {
          setTimeout(() => reject(new Error('Ongoing request timeout')), 5000);
        });

        return await Promise.race([
          this.ongoingPermissionRequest,
          timeoutPromise,
        ]);
      } catch (_error) {
        this.ongoingPermissionRequest = null; // Clear the stuck request
        // Continue to start a new request below
      }
    }

    // Create and store the permission request promise
    this.ongoingPermissionRequest = this.performPermissionRequest();

    try {
      const result = await this.ongoingPermissionRequest;
      return result;
    } catch (_error) {
      return false;
    } finally {
      this.ongoingPermissionRequest = null; // Clear the ongoing request
    }
  }
  private async performPermissionRequest(): Promise<boolean> {
    if ('Notification' in window) {
      // Require secure context (except localhost) and top-level browsing context
      if (!isSecureContext && location.hostname !== 'localhost') {
        this.loggingService.logWarning(
          'Notifications require a secure context (HTTPS).',
        );
        this.notificationPermission = Notification.permission;
        this.permissionState$.next(this.notificationPermission);
        return false;
      }

      if (this.isInIframe()) {
        this.loggingService.logWarning(
          'Notifications permission cannot be requested from an embedded frame.',
        );
        this.notificationPermission = Notification.permission;
        this.permissionState$.next(this.notificationPermission);
        return false;
      }

      if (Notification.permission === 'granted') {
        return true;
      }

      if (Notification.permission === 'denied') {
        this.loggingService.logWarning(
          'Notifications are blocked by the browser. User must enable permissions in site settings.',
        );
        this.notificationPermission = 'denied';
        this.permissionState$.next('denied');
        return false;
      }

      try {
        // Cross-browser permission request without artificial timeouts
        let permission: NotificationPermission = Notification.permission;

        if (typeof Notification.requestPermission === 'function') {
          const req = Notification.requestPermission as unknown as (
            cb?: (status: NotificationPermission) => void,
          ) => Promise<NotificationPermission> | void;

          if ((Notification.requestPermission as any).length === 1) {
            // Callback-style (Safari)
            permission = await new Promise<NotificationPermission>(
              (resolve, reject) => {
                try {
                  (req as any)((status: NotificationPermission) =>
                    resolve(status),
                  );
                } catch (e) {
                  reject(e);
                }
              },
            );
          } else {
            const result = req();
            if (result && typeof (result as any).then === 'function') {
              permission = await (result as Promise<NotificationPermission>);
            } else {
              permission = Notification.permission;
            }
          }
        } else {
          this.loggingService.logWarning(
            'Notification.requestPermission() is not available in this context.',
          );
          this.notificationPermission = Notification.permission;
          this.permissionState$.next(this.notificationPermission);
          return false;
        }

        this.notificationPermission = permission;
        this.permissionState$.next(permission);

        const granted = permission === 'granted';

        if (granted) {
          this.showSuccess(
            'Notifications',
            'Notifications enabled successfully!',
          );
          this.showDesktopNotification({
            title: 'Scan Ninja',
            body: 'Notifications are now enabled!',
            icon: '/favicon.ico',
          });
        } else if (permission === 'denied') {
          this.loggingService.logWarning(
            'Notification permission denied by user.',
          );
        } else {
          // Still default: user dismissed or browser used quieter UI; no toasts
        }

        return granted;
      } catch (error) {
        // Quiet-fail: log and return false
        this.loggingService.logWarning(
          'Notification permission request did not complete.',
          error as any,
        );
        this.notificationPermission = Notification.permission;
        this.permissionState$.next(this.notificationPermission);
        return false;
      }
    } else {
      if (!this.warnedUnsupported) {
        this.loggingService.logWarning(
          '❌ Notifications not supported in this browser',
        );
        this.warnedUnsupported = true;
      }
      this.notificationPermission = 'denied';
      this.permissionState$.next('denied');
      return false;
    }
  }

  /**
   * Check if desktop notifications are supported
   * @returns boolean - true if desktop notifications are supported by the browser, false otherwise
   */
  isDesktopNotificationSupported(): boolean {
    const isSupported = 'Notification' in window;
    return isSupported;
  }

  /**
   * Get all active in-app toast notifications
   * @returns Observable<ToastNotification[]> - Observable stream of active toast notifications
   */
  getNotifications(): Observable<ToastNotification[]> {
    return this.notifications$.asObservable();
  }

  /**
   * Show a success desktop notification
   * @param title - The title of the notification
   * @param message - The message content of the notification
   * @param duration - Optional duration in milliseconds (0 = permanent, default = 6000)
   * @param action - Optional action with label and callback function
   * @returns string - Unique ID of the notification
   */
  showSuccess(
    title: string,
    message: string,
    duration?: number,
    action?: { label: string; callback: () => void },
  ): string {
    // Try desktop notification first
    const desktopSuccess = this.showDesktopNotification({
      title: `✅ ${title}`,
      body: message,
      icon: '/assets/images/logo/scan-ninja-icon.png', // Use existing icon
      tag: `success-${Date.now()}`, // Make each notification unique
      requireInteraction: false, // Allow auto-dismissal
      data: {
        duration: duration || this.defaultDuration,
        callback: action?.callback,
      },
    });

    // Only show in-app notification if desktop notification failed
    const id = this.generateId();
    if (!desktopSuccess) {
      this.loggingService.logWarning(
        '🔄 Desktop notification failed, showing in-app notification as fallback',
      );
      this.addInAppNotification(
        'success',
        title,
        message,
        duration,
        action,
        id,
      );
    }

    return id;
  }

  /**
   * Show an error desktop notification
   * @param title - The title of the notification
   * @param message - The message content of the notification
   * @param duration - Optional duration in milliseconds (0 = permanent, default = 12000 for errors)
   * @param action - Optional action with label and callback function
   * @returns string - Unique ID of the notification
   */
  showError(
    title: string,
    message: string,
    duration?: number,
    action?: { label: string; callback: () => void },
  ): string {
    // Try desktop notification first
    const desktopSuccess = this.showDesktopNotification({
      title: `❌ ${title}`,
      body: message,
      icon: '/assets/images/logo/scan-ninja-icon.png', // Use existing icon
      tag: `error-${Date.now()}`, // Make each notification unique
      requireInteraction: false, // Allow auto-dismissal even for errors
      data: {
        duration: duration !== undefined ? duration : this.defaultDuration * 2,
        callback: action?.callback,
      }, // Errors stay longer
    });

    // Only show in-app notification if desktop notification failed
    const id = this.generateId();
    if (!desktopSuccess) {
      this.loggingService.logWarning(
        '🔄 Desktop notification failed, showing in-app notification as fallback',
      );
      this.addInAppNotification(
        'error',
        title,
        message,
        duration || 0,
        action,
        id,
      );
    }

    return id;
  }

  /**
   * Show a warning desktop notification
   * @param title - The title of the notification
   * @param message - The message content of the notification
   * @param duration - Optional duration in milliseconds (0 = permanent, default = 9000 for warnings)
   * @param action - Optional action with label and callback function
   * @returns string - Unique ID of the notification
   */
  showWarning(
    title: string,
    message: string,
    duration?: number,
    action?: { label: string; callback: () => void },
  ): string {
    // Try desktop notification first
    const desktopSuccess = this.showDesktopNotification({
      title: `⚠️ ${title}`,
      body: message,
      icon: '/assets/images/logo/scan-ninja-icon.png', // Use existing icon
      tag: `warning-${Date.now()}`, // Make each notification unique
      requireInteraction: false, // Allow auto-dismissal
      data: {
        duration:
          duration !== undefined ? duration : this.defaultDuration * 1.5,
        callback: action?.callback,
      }, // Warnings stay a bit longer
    });

    // Only show in-app notification if desktop notification failed
    const id = this.generateId();
    if (!desktopSuccess) {
      this.loggingService.logWarning(
        '🔄 Desktop notification failed, showing in-app notification as fallback',
      );
      this.addInAppNotification(
        'warning',
        title,
        message,
        duration,
        action,
        id,
      );
    }

    return id;
  }

  /**
   * Show an info desktop notification
   * @param title - The title of the notification
   * @param message - The message content of the notification
   * @param duration - Optional duration in milliseconds (0 = permanent, default = 6000)
   * @param action - Optional action with label and callback function
   * @returns string - Unique ID of the notification
   */
  showInfo(
    title: string,
    message: string,
    duration?: number,
    action?: { label: string; callback: () => void },
  ): string {
    // Try desktop notification first
    const desktopSuccess = this.showDesktopNotification({
      title: `ℹ️ ${title}`,
      body: message,
      icon: '/assets/images/logo/scan-ninja-icon.png', // Use existing icon
      tag: `info-${Date.now()}`, // Make each notification unique
      requireInteraction: false, // Allow auto-dismissal
      data: {
        duration: duration || this.defaultDuration,
        callback: action?.callback,
      },
    });

    // Only show in-app notification if desktop notification failed
    const id = this.generateId();
    if (!desktopSuccess) {
      this.loggingService.logWarning(
        '🔄 Desktop notification failed, showing in-app notification as fallback',
      );
      this.addInAppNotification('info', title, message, duration, action, id);
    }

    return id;
  }

  /**
   * Show a desktop notification with custom options
   * @param options - Custom notification options including title, body, icon, etc.
   * @returns boolean - true if notification was successfully created, false otherwise
   */
  showCustomDesktopNotification(options: DesktopNotificationOptions): boolean {
    return this.showDesktopNotification(options);
  }

  /**
   * Show desktop notification using Web Notifications API
   * @param options - Notification options including title, body, icon, etc.
   * @returns boolean - true if notification was successfully created, false otherwise
   */
  private showDesktopNotification(
    options: DesktopNotificationOptions,
  ): boolean {
    // Always sync with latest browser permission before attempting
    if (typeof window !== 'undefined' && 'Notification' in window) {
      const latest = Notification.permission;
      if (latest !== this.notificationPermission) {
        this.notificationPermission = latest;
        this.permissionState$.next(latest);
      }
    }

    if (!this.isDesktopNotificationSupported()) {
      if (!this.warnedUnsupported) {
        this.loggingService.logWarning(
          '❌ Desktop notifications are not supported in this browser',
        );
        this.warnedUnsupported = true;
      }
      return false;
    }

    if (this.notificationPermission !== 'granted') {
      if (!this.warnedPermissionNotGranted) {
        this.loggingService.logWarning(
          '❌ Desktop notification permission not granted. Current permission:',
          this.notificationPermission,
        );
        this.warnedPermissionNotGranted = true;
      }
      return false;
    }

    try {
      // Reset throttling flag once a notification is successfully shown
      this.warnedPermissionNotGranted = false;

      // Store callback separately to avoid DataCloneError
      const callbackId = options.data?.callback ? this.generateId() : null;
      if (callbackId && options.data?.callback) {
        this.pendingCallbacks.set(callbackId, options.data.callback);
      }

      // Generate cryptographically secure random tag (3 bytes = 6 hex chars)
      const randomBytes = new Uint8Array(3);
      window.crypto.getRandomValues(randomBytes);
      const securePart = Array.from(randomBytes)
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');

      const notification = new Notification(options.title, {
        body: options.body,
        icon: options.icon || '/assets/images/logo/scan-ninja-icon.png',
        badge: '/assets/images/logo/scan-ninja-icon.png',
        tag: options.tag || `scan-ninja-${Date.now()}-${securePart}`,
        data: {
          duration: options.data?.duration,
          callbackId: callbackId,
        },
        requireInteraction: options.requireInteraction || false,
        silent: false,
      });

      // Handle notification click
      notification.onclick = (event) => {
        event.preventDefault();
        window.focus();
        notification.close();
        const callbackId = notification.data?.callbackId;
        if (callbackId && this.pendingCallbacks.has(callbackId)) {
          const callback = this.pendingCallbacks.get(callbackId);
          if (callback) {
            callback();
            this.pendingCallbacks.delete(callbackId);
          }
        }
      };

      // Handle notification close
      notification.onclose = () => {
        const callbackId = notification.data?.callbackId;
        if (callbackId && this.pendingCallbacks.has(callbackId)) {
          this.pendingCallbacks.delete(callbackId);
        }
      };

      // Handle notification errors
      notification.onerror = (error) => {
        this.loggingService.logError('💥 Desktop notification error:', error);
      };

      // Auto-close notification after duration (if specified and > 0)
      const duration = notification.data?.duration;
      if (duration && duration > 0) {
        setTimeout(() => {
          try {
            notification.close();
          } catch {
            /* noop */
          }
        }, duration);
      }

      return true;
    } catch (error) {
      this.loggingService.logError(
        '💥 Failed to show desktop notification:',
        error,
      );
      return false;
    }
  }

  /**
   * Remove a specific in-app notification by ID
   * @param id - The unique ID of the notification to remove
   */
  removeNotification(id: string): void {
    const currentNotifications = this.notifications$.value;
    const updatedNotifications = currentNotifications.filter(
      (n) => n.id !== id,
    );
    this.notifications$.next(updatedNotifications);
  }

  /**
   * Clear all in-app notifications
   */
  clearAll(): void {
    this.notifications$.next([]);
  }

  /**
   * Add a new in-app notification (fallback for when desktop notifications are not available)
   * @param type - The type of notification ('success', 'error', 'warning', 'info')
   * @param title - The title of the notification
   * @param message - The message content of the notification
   * @param duration - Optional duration in milliseconds (0 = permanent)
   * @param action - Optional action with label and callback function
   * @param customId - Optional custom ID for the notification
   * @returns string - Unique ID of the notification
   */
  private addInAppNotification(
    type: 'success' | 'error' | 'warning' | 'info',
    title: string,
    message: string,
    duration?: number,
    action?: { label: string; callback: () => void },
    customId?: string,
  ): string {
    const id = customId || this.generateId();
    const notification: ToastNotification = {
      id,
      title,
      message,
      type,
      duration: duration !== undefined ? duration : this.defaultDuration,
      action,
      timestamp: new Date(),
    };

    const currentNotifications = this.notifications$.value;
    this.notifications$.next([...currentNotifications, notification]);

    // Auto-remove after duration (if not permanent)
    if (notification.duration && notification.duration > 0) {
      setTimeout(() => {
        this.removeNotification(id);
      }, notification.duration);
    }

    return id;
  }

  /**
   * Generate unique ID for notifications using cryptographically secure random
   * @returns string - Unique identifier for the notification
   */
  private generateId(): string {
    // 6 bytes = 48 bits entropy, hex encoding = 12 chars
    const randomBytes = new Uint8Array(6);
    window.crypto.getRandomValues(randomBytes);
    const securePart = Array.from(randomBytes)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
    return 'toast_' + Date.now() + '_' + securePart;
  }

  /**
   * Reset permission request state (for debugging/recovery)
   */
  resetPermissionRequest(): void {
    this.ongoingPermissionRequest = null;
  }

  private isInIframe(): boolean {
    try {
      return window.self !== window.top;
    } catch {
      return true;
    }
  }
}
